interface Meta {
  [key: string]: any;
}

function logError(defName: string, field?: string, msg?: string): void {
  // eslint-disable-next-line no-console
  console.error(`${defName}${field}: ${msg}.`);
}

interface Validator {
  str: (name: string, mandatory?: boolean, depends?: string[]) => Validator;
  bool: (name: string, mandatory?: boolean, depends?: string[]) => Validator;
  number: (name: string, mandatory?: boolean, depends?: string[]) => Validator;
  custom: (name: string, mandatory: boolean, fn: (value: any, prop?: string) => boolean) => Validator;
  validator: Function;
}

/**
* Helper to generate a validator to check the decorators meta info.
* There are other util methods too.
*/
export default class MetaValidator {

  static logError = logError;

  /**
  * Create a validator to check the decorators meta info.
  */
  static create(): Validator {
    const meta: Meta = {};
    const self: Validator = {
      str: (name: string, mandatory?: boolean, depends?: string[]) => newProp(name, 'string', mandatory, depends),
      bool: (name: string, mandatory?: boolean, depends?: string[]) => newProp(name, 'boolean', mandatory, depends),
      number: (name: string, mandatory?: boolean, depends?: string[]) => newProp(name, 'number', mandatory, depends),
      custom: (name: string, mandatory: boolean, fn: (value: any, prop?: string) => any) => newProp(name, 'custom', mandatory, fn),
      validator: () => {
        return createValidator(meta);
      },
    };

    function newProp(name: string, type: string, mandatory = false, depends: string[]|Function = []): Validator {
      meta[name] = { type, mandatory, depends };
      return self;
    }
    return self;
  }

  /**
  * Validate if the decorator structure is right.
  * @param {function} fn the decorator being applyed.
  * @param {*} paramsValidator function that validate params.
  * @param {*} definition tha class receiving the decorator.
  * @param {*} params the received params.
  */
  static validateDecorator(fn: Function, paramsValidator: Function, definition: Function, params: any = {}): any|void {
    const name = fn.name;

    MetaValidator.checkDef(definition, name);

    if (definition.constructor === Object) {
      return (...args: any[]) => fn(...args, definition);
    }

    paramsValidator(params, definition, name);
  }

  /**
  * Check if decorator is being invoked without params.
  *
  * @param {class} definition the receiving the decorator.
  * @param {*} name decorator name.
  */
  static checkDef(definition: Function, name: string): void {
    if (!definition) {
      logError(name, '', 'If there are no params, remove the ()');
    }
  }

}

////////
// UTILS
////////

function createValidator(knewProps: Meta): Function {
  const props = Object.getOwnPropertyNames(knewProps);
  const mandatories = props.filter(p => knewProps[p].mandatory);
  return (foundParams: string[], definition: Function, field: string) => {
    const isFunction = typeof definition === 'function';
    const defName = isFunction ? definition.name : definition.constructor.name;
    validateParams(knewProps, mandatories, foundParams, defName, field);
  };
}

function validateParams(knewProps: Meta, mandatories: string[], foundParams: Meta, defName: string, field = ''): void {
  if (field) {
    field = `[${field}]`;
  }

  const props = Object.getOwnPropertyNames(foundParams);
  props.forEach(prop => {
    const tp = knewProps[prop];

    if (!tp) {
      logError(defName, field, `unknow property '${prop}'`);
    }

    if (tp.type === 'custom') {
      const valid = tp.depends(foundParams[prop], prop);
      if (!valid) {
        logError(defName, field, 'invalid prop value.');
      }
      return;
    }

    const typeOfParam = typeof foundParams[prop];

    if (tp && typeOfParam !== tp.type) {
      logError(defName, field, `expected property '${prop}' as '${tp.type}', found '${typeOfParam}'`);
    }

    if (tp && tp.depends) {
      tp.depends.forEach((dep: string) => {
        if (!props.includes(dep)) {
          logError(defName, field, `when has '${prop}', props '${tp.depends}' are mandatory`);
        }
      });
    }
  });

  mandatories.forEach((prop: string) => {
    if (foundParams[prop] === undefined) {
      logError(defName, field, `mandatory property '${prop}' not found`);
    }
  });
}
